<template>
  <main class="admin-result-view">
    <V2RequestProcessor
      v-if="requestKind === 'v2'"
      :request-uuid-override="normalizedRequestId"
      :view-override="requestedView"
      :admin-mode="true"
    />

    <ScreenshotViewer
      v-else-if="requestKind === 'legacy'"
      :requestID="normalizedRequestId"
      mode="admin"
    />

    <section v-else class="admin-result-error" role="alert">
      <p class="admin-result-error__eyebrow">Request review</p>
      <h1>Invalid request identifier</h1>
      <p>
        This link does not contain a valid legacy request ID or V2 request UUID.
        Return to Request Monitor and open the request again.
      </p>
      <button type="button" @click="returnToMonitor">
        Back to Request Monitor
      </button>
    </section>
  </main>
</template>

<script>
import ScreenshotViewer from './ScreenshotViewer.vue';
import V2RequestProcessor from './V2RequestProcessor.vue';

const V2_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LEGACY_ID = /^[1-9][0-9]*$/;

function safeDecode(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';

  try {
    return decodeURIComponent(text).trim();
  } catch (error) {
    return text;
  }
}

function parseRequestIdentifier(value) {
  const raw = safeDecode(value);
  const lower = raw.toLowerCase();

  const v2Prefixes = ['v2:', 'casebuddy_lite:', 'casebuddy-lite:'];
  for (const prefix of v2Prefixes) {
    if (lower.startsWith(prefix)) {
      const requestUuid = raw.slice(prefix.length).trim();
      return V2_UUID.test(requestUuid)
        ? { kind: 'v2', id: requestUuid.toLowerCase() }
        : { kind: 'invalid', id: '' };
    }
  }

  const legacyPrefixes = ['v1:', 'legacy:'];
  for (const prefix of legacyPrefixes) {
    if (lower.startsWith(prefix)) {
      const requestId = raw.slice(prefix.length).trim();
      return LEGACY_ID.test(requestId)
        ? { kind: 'legacy', id: requestId }
        : { kind: 'invalid', id: '' };
    }
  }

  if (V2_UUID.test(raw)) {
    return { kind: 'v2', id: raw.toLowerCase() };
  }

  if (LEGACY_ID.test(raw)) {
    return { kind: 'legacy', id: raw };
  }

  return { kind: 'invalid', id: '' };
}

export default {
  name: 'AdminViewImage',
  components: {
    ScreenshotViewer,
    V2RequestProcessor,
  },
  props: {
    requestID: {
      type: [String, Number],
      required: true,
    },
  },
  computed: {
    parsedRequestIdentifier() {
      return parseRequestIdentifier(this.requestID);
    },
    requestKind() {
      return this.parsedRequestIdentifier.kind;
    },
    normalizedRequestId() {
      return this.parsedRequestIdentifier.id;
    },
    requestedView() {
      if (this.requestKind !== 'v2') return '';

      const value = String(this.$route.query.view || 'visual')
        .trim()
        .toLowerCase();

      return ['result', 'report', 'visual', 'image'].includes(value)
        ? value
        : 'visual';
    },
  },
  methods: {
    returnToMonitor() {
      this.$router.push({ name: 'RequestMonitor' }).catch(() => {
        this.$router.back();
      });
    },
  },
};
</script>

<style scoped>
.admin-result-view {
  min-height: 100vh;
  padding: 1rem;
  background: #f3f7fb;
}

.admin-result-error {
  width: min(720px, calc(100% - 2rem));
  margin: 4rem auto;
  padding: 2rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.admin-result-error__eyebrow {
  margin: 0 0 0.35rem;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.admin-result-error h1 {
  margin: 0 0 0.75rem;
  color: #0f172a;
}

.admin-result-error p {
  color: #475569;
  line-height: 1.65;
}

.admin-result-error button {
  margin-top: 1rem;
  padding: 0.7rem 1rem;
  border: 0;
  border-radius: 10px;
  background: #2563eb;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
}
</style>
